import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  CheckCircle2,
  CreditCard,
  Cpu,
  ExternalLink,
  FileText,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Settings,
  Users,
} from 'lucide-react';
import dayjs from 'dayjs';
import { Alert, Badge, Button, Card, CardHeader, CardTitle } from '@components/ui';
import { cn } from '@lib/utils/cn';
import { billingApi } from '@features/settings/api/billingApi';
import type { BillingCurrent, SubscriptionStatus } from '@features/settings/types';

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(value: string | undefined | null, isRtl: boolean): string | null {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(isRtl ? 'ar-EG-u-nu-latn' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return value;
  }
}

type Tone = 'success' | 'warning' | 'danger' | 'default';

function resolveStatus(data: BillingCurrent | undefined): SubscriptionStatus {
  if (!data) return 'no_license';
  if (data.status) return data.status;
  return data.active ? 'active' : 'no_license';
}

const STATUS_META: Record<SubscriptionStatus, { ar: string; en: string; tone: Tone }> = {
  active: { ar: 'نشط', en: 'Active', tone: 'success' },
  expiring: { ar: 'قارب على الانتهاء', en: 'Expiring soon', tone: 'warning' },
  expired: { ar: 'منتهٍ', en: 'Expired', tone: 'danger' },
  no_license: { ar: 'لا يوجد اشتراك', en: 'No subscription', tone: 'default' },
};

function UsageBar({
  icon,
  label,
  current,
  max,
  isRtl,
}: {
  icon: React.ReactNode;
  label: string;
  current: number | undefined;
  max: number | undefined;
  isRtl: boolean;
}) {
  const used = current ?? 0;
  const limit = max ?? 0;
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone: Tone = pct >= 100 ? 'danger' : pct >= 85 ? 'warning' : 'success';
  const barColor =
    tone === 'danger' ? 'bg-danger' : tone === 'warning' ? 'bg-warning' : 'bg-success';
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-fg">
          <span className="text-fg-muted">{icon}</span>
          {label}
        </span>
        <span className="font-mono text-sm">
          <span className={cn('font-semibold', pct >= 100 && 'text-danger')}>{used}</span>
          <span className="text-fg-muted"> / {limit || '∞'}</span>
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-bg-muted overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-fg-muted">
        {limit > 0
          ? isRtl
            ? `المتبقي: ${Math.max(0, limit - used)}`
            : `Remaining: ${Math.max(0, limit - used)}`
          : isRtl
            ? 'غير محدود'
            : 'Unlimited'}
      </div>
    </div>
  );
}

function AddonCounter({
  label,
  qty,
  onChange,
  onPay,
  paying,
  isRtl,
}: {
  label: string;
  qty: number;
  onChange: (next: number) => void;
  onPay: () => void;
  paying: boolean;
  isRtl: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface p-3">
      <span className="text-sm font-medium flex-1 min-w-[120px]">{label}</span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" onClick={() => onChange(Math.max(0, qty - 1))} aria-label="decrease">
          <Minus className="h-3 w-3" />
        </Button>
        <span className="w-10 text-center font-mono">{qty}</span>
        <Button variant="outline" size="sm" onClick={() => onChange(qty + 1)} aria-label="increase">
          <Plus className="h-3 w-3" />
        </Button>
      </div>
      <Button size="sm" onClick={onPay} loading={paying} disabled={qty <= 0}>
        <CreditCard className="h-3.5 w-3.5" />
        {isRtl ? 'دفع' : 'Pay'}
      </Button>
    </div>
  );
}

// ── page ────────────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const current = useQuery({
    queryKey: ['billing', 'current'],
    queryFn: billingApi.current,
    staleTime: 60_000,
    retry: false,
  });
  const quote = useQuery({
    queryKey: ['billing', 'quote'],
    queryFn: billingApi.quote,
    staleTime: 5 * 60_000,
  });

  const [selectedPkg, setSelectedPkg] = useState<string | null>(null);
  const [empAddon, setEmpAddon] = useState(0);
  const [devAddon, setDevAddon] = useState(0);

  const checkout = useMutation({
    mutationFn: (packageId: string) => billingApi.createCheckoutSession({ packageId }),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });
  const empCheckout = useMutation({
    mutationFn: (qty: number) => billingApi.createAddonSession({ type: 'employees', qty }),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });
  const devCheckout = useMutation({
    mutationFn: (qty: number) => billingApi.createAddonSession({ type: 'devices', qty }),
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
  });
  const invoices = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: billingApi.invoices,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const portal = useMutation({
    mutationFn: billingApi.createPortalSession,
    onSuccess: ({ url }) => {
      if (url) window.open(url, '_blank');
    },
  });

  const sub = current.data;
  const status = resolveStatus(sub);
  const meta = STATUS_META[status];
  const issuedFmt = useMemo(() => fmtDate(sub?.issuedAt, isRtl), [sub?.issuedAt, isRtl]);
  const expiresFmt = useMemo(() => fmtDate(sub?.expiresAt, isRtl), [sub?.expiresAt, isRtl]);
  const days = sub?.daysRemaining;

  // Term-progress percentage (elapsed of the subscription period).
  const elapsedPct = useMemo(() => {
    if (!sub?.totalDays || sub.totalDays <= 0 || days == null) return null;
    const remaining = Math.max(0, days);
    return Math.min(100, Math.max(0, Math.round(((sub.totalDays - remaining) / sub.totalDays) * 100)));
  }, [sub?.totalDays, days]);

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center bg-brand-muted text-brand">
          <CreditCard className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-bold">
            {isRtl ? 'الفواتير والاشتراك' : 'Billing & Subscription'}
          </h1>
          <p className="text-xs text-fg-muted">
            {isRtl
              ? 'إدارة الخطة، السعة، الفواتير، والاشتراك الحالي'
              : 'Manage your plan, capacity, invoices, and current subscription'}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => current.refetch()} disabled={current.isFetching}>
          <RefreshCw className={cn('h-3.5 w-3.5', current.isFetching && 'animate-spin')} />
        </Button>
      </div>

      {/* Status banner */}
      {status === 'expired' && (
        <Alert tone="danger">
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {isRtl
              ? `انتهى اشتراكك${expiresFmt ? ` بتاريخ ${expiresFmt}` : ''}. جدّد الاشتراك لاستعادة الوصول الكامل للنظام.`
              : `Your subscription expired${expiresFmt ? ` on ${expiresFmt}` : ''}. Renew to restore full access.`}
          </span>
        </Alert>
      )}
      {status === 'expiring' && days != null && (
        <Alert tone="warning">
          <span className="inline-flex items-center gap-2">
            <CalendarClock className="h-4 w-4 shrink-0" />
            {isRtl
              ? `اشتراكك ينتهي خلال ${days} ${days === 1 ? 'يوم' : 'يوماً'}${expiresFmt ? ` (${expiresFmt})` : ''}. جدّد مبكراً لتجنّب انقطاع الخدمة.`
              : `Your subscription expires in ${days} day${days === 1 ? '' : 's'}${expiresFmt ? ` (${expiresFmt})` : ''}. Renew early to avoid interruption.`}
          </span>
        </Alert>
      )}

      {/* Current subscription */}
      <Card className="p-5">
        <CardHeader className="p-0 mb-4 flex items-center justify-between">
          <CardTitle className="text-base">{isRtl ? 'الاشتراك الحالي' : 'Current Subscription'}</CardTitle>
          {!current.isLoading && !current.isError && (
            <Badge tone={meta.tone}>
              {status === 'active' && <CheckCircle2 className="h-3 w-3 inline-block me-1" />}
              {status === 'expired' && <AlertTriangle className="h-3 w-3 inline-block me-1" />}
              {isRtl ? meta.ar : meta.en}
            </Badge>
          )}
        </CardHeader>

        {current.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isRtl ? 'جاري التحميل...' : 'Loading...'}
          </div>
        ) : current.isError ? (
          <p className="text-sm text-danger">{isRtl ? 'تعذر تحميل الاشتراك' : 'Failed to load subscription'}</p>
        ) : status === 'no_license' ? (
          <p className="text-sm text-fg-muted">{isRtl ? 'لا يوجد اشتراك نشط' : 'No active subscription'}</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-fg-muted">{isRtl ? 'الخطة' : 'Plan'}</div>
                <div className="font-semibold">{sub?.planName || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">{isRtl ? 'بداية الاشتراك' : 'Started on'}</div>
                <div className="font-semibold">{issuedFmt || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">{isRtl ? 'تاريخ الانتهاء' : 'Expires on'}</div>
                <div className={cn('font-semibold', status === 'expired' && 'text-danger')}>
                  {expiresFmt || '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">{isRtl ? 'المتبقي' : 'Remaining'}</div>
                <div
                  className={cn(
                    'font-semibold',
                    status === 'expired' ? 'text-danger' : status === 'expiring' ? 'text-warning' : 'text-success',
                  )}
                >
                  {days == null
                    ? '—'
                    : days < 0
                      ? isRtl
                        ? `منتهٍ منذ ${Math.abs(days)} يوم`
                        : `${Math.abs(days)}d ago`
                      : isRtl
                        ? `${days} ${days === 1 ? 'يوم' : 'يوماً'}`
                        : `${days} day${days === 1 ? '' : 's'}`}
                </div>
              </div>
            </div>

            {/* Term progress */}
            {elapsedPct != null && (
              <div>
                <div className="flex items-center justify-between text-[11px] text-fg-muted mb-1">
                  <span>{isRtl ? 'مدة الاشتراك' : 'Subscription term'}</span>
                  <span className="font-mono">{elapsedPct}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-bg-muted overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      status === 'expired' ? 'bg-danger' : status === 'expiring' ? 'bg-warning' : 'bg-brand',
                    )}
                    style={{ width: `${elapsedPct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Usage / capacity */}
      {status !== 'no_license' && !current.isError && (
        <Card className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="text-base">{isRtl ? 'السعة والاستخدام' : 'Capacity & Usage'}</CardTitle>
          </CardHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <UsageBar
              icon={<Users className="h-4 w-4" />}
              label={isRtl ? 'الموظفون' : 'Employees'}
              current={sub?.currentEmployees}
              max={sub?.maxEmployees}
              isRtl={isRtl}
            />
            <UsageBar
              icon={<Cpu className="h-4 w-4" />}
              label={isRtl ? 'الأجهزة' : 'Devices'}
              current={sub?.currentDevices}
              max={sub?.maxDevices}
              isRtl={isRtl}
            />
          </div>
        </Card>
      )}

      {/* Available plans */}
      <Card className="p-5">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="text-base">{isRtl ? 'الخطط المتوفرة' : 'Available Plans'}</CardTitle>
        </CardHeader>
        {quote.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isRtl ? 'جاري تحميل الخطط...' : 'Loading plans...'}
          </div>
        ) : quote.isError ? (
          <p className="text-sm text-danger">{isRtl ? 'تعذر تحميل الخطط' : 'Failed to load plans'}</p>
        ) : quote.data?.packages?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {quote.data.packages.map((p) => {
              const selected = selectedPkg === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPkg(p.id)}
                  className={cn(
                    'text-start rounded-lg border p-4 transition-all',
                    selected
                      ? 'border-brand bg-brand-muted/30 shadow-sm'
                      : 'border-border hover:border-brand/60 hover:bg-bg-muted/40',
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold">{p.name}</div>
                    {selected && (
                      <span className="text-brand">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-fg-muted space-y-0.5">
                    <div>{isRtl ? 'المدة: ' : 'Duration: '} {p.duration} {isRtl ? 'شهر' : 'mo'}</div>
                    <div>{isRtl ? 'الموظفون: ' : 'Employees: '} {p.employees}</div>
                    <div>{isRtl ? 'الأجهزة: ' : 'Devices: '} {p.devices}</div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-fg-muted">{isRtl ? 'لا توجد خطط متوفرة حالياً' : 'No packages available'}</p>
        )}
        {selectedPkg && (
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button onClick={() => checkout.mutate(selectedPkg)} loading={checkout.isPending}>
              <CreditCard className="h-4 w-4" />
              {isRtl ? 'ادفع الآن' : 'Pay now'}
            </Button>
          </div>
        )}
        {checkout.isError && (
          <p className="mt-2 text-sm text-danger">
            {isRtl ? 'تعذر إنشاء جلسة الدفع' : 'Failed to create checkout session'}
          </p>
        )}
      </Card>

      {/* Stripe portal */}
      <Card className="p-5">
        <CardHeader className="p-0 mb-3 flex items-center justify-between">
          <CardTitle className="text-base">{isRtl ? 'إدارة الاشتراك عبر Stripe' : 'Manage subscription via Stripe'}</CardTitle>
        </CardHeader>
        <p className="text-xs text-fg-muted mb-3">
          {isRtl
            ? 'افتح لوحة Stripe لإدارة وسائل الدفع، تحديث بيانات الفواتير، وعرض التاريخ الكامل.'
            : 'Open the Stripe portal to manage payment methods, update billing info, and view history.'}
        </p>
        <Button onClick={() => portal.mutate()} loading={portal.isPending} disabled={portal.isPending} variant="outline">
          <Settings className="h-4 w-4" />
          {isRtl ? 'فتح لوحة Stripe' : 'Open Stripe portal'}
          <ExternalLink className="h-3 w-3" />
        </Button>
        {portal.isError && (
          <p className="mt-2 text-sm text-warning">
            {isRtl ? 'لا يوجد حساب Stripe مربوط بهذه الشركة.' : 'No Stripe customer linked to this company.'}
          </p>
        )}
      </Card>

      {/* Invoices */}
      <Card className="p-5">
        <CardHeader className="p-0 mb-3 flex items-center justify-between">
          <CardTitle className="text-base">
            <FileText className="h-4 w-4 inline-block me-1" />
            {isRtl ? 'سجل الفواتير' : 'Invoice history'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => invoices.refetch()} disabled={invoices.isFetching}>
            <RefreshCw className={cn('h-3.5 w-3.5', invoices.isFetching && 'animate-spin')} />
          </Button>
        </CardHeader>
        {invoices.isLoading ? (
          <p className="text-sm text-fg-muted">{isRtl ? 'جاري التحميل...' : 'Loading...'}</p>
        ) : !invoices.data?.invoices?.length ? (
          <p className="text-sm text-fg-muted">{isRtl ? 'لا توجد فواتير' : 'No invoices yet'}</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-start text-xs font-semibold">{isRtl ? 'التاريخ' : 'Date'}</th>
                  <th className="px-3 py-2 text-start text-xs font-semibold">#</th>
                  <th className="px-3 py-2 text-start text-xs font-semibold">{isRtl ? 'المبلغ' : 'Amount'}</th>
                  <th className="px-3 py-2 text-start text-xs font-semibold">{isRtl ? 'الحالة' : 'Status'}</th>
                  <th className="px-3 py-2 text-start text-xs font-semibold">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-border">
                    <td className="px-3 py-2 text-xs">{dayjs(inv.created).format('YYYY-MM-DD')}</td>
                    <td className="px-3 py-2 font-mono text-xs">{inv.number ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {(inv.amount_paid / 100).toFixed(2)} {inv.currency?.toUpperCase()}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'default'}>
                        {inv.status ?? '—'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {inv.invoice_pdf ? (
                        <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline">
                          PDF ↗
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add-ons */}
      <Card className="p-5">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="text-base">{isRtl ? 'إضافات' : 'Add-ons'}</CardTitle>
          <p className="text-xs text-fg-muted mt-1">
            {isRtl ? 'زيادة السعة ضمن الاشتراك الحالي' : 'Increase capacity on your current subscription'}
          </p>
        </CardHeader>
        <div className="space-y-2">
          <AddonCounter
            label={isRtl ? 'موظفون إضافيون' : 'Extra employees'}
            qty={empAddon}
            onChange={setEmpAddon}
            onPay={() => empCheckout.mutate(empAddon)}
            paying={empCheckout.isPending}
            isRtl={isRtl}
          />
          <AddonCounter
            label={isRtl ? 'أجهزة إضافية' : 'Extra devices'}
            qty={devAddon}
            onChange={setDevAddon}
            onPay={() => devCheckout.mutate(devAddon)}
            paying={devCheckout.isPending}
            isRtl={isRtl}
          />
        </div>
      </Card>
    </div>
  );
}
